import {
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Chip,
  Box,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOffIcon from '@mui/icons-material/PersonOff';

import { CommentDto, CommentStatus } from '../CommentsClient';
import { CommentsService } from '../Comments2';
import { timeAgo } from '../../../utils';
import {
  Avatar,
  Username,
  CreatedDate,
} from './styles';
import { useUserState } from 'src/contexts/UserContext';

function formatDate(iso?: string | null): string {
  if (iso == null || iso === '') return '';
  try {
    return new Date(iso).toLocaleString('pl');
  } catch {
    return '';
  }
}

function ModerationBadge({ comment }: { comment: CommentDto }) {
  if (comment.hidden || comment.status === CommentStatus.hidden) {
    const who = comment.hiddenBy?.name ?? comment.hiddenByUser?.name ?? 'moderacja';
    const when = formatDate(comment.hiddenAt);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Chip
          icon={<VisibilityOffIcon fontSize="small" />}
          label={`Ukryty przez ${who}${when ? ` • ${when}` : ''}`}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: 11 }}
        />
      </Box>
    );
  }
  if (
    comment.status === CommentStatus.deletedByUser ||
    comment.status === CommentStatus.deletedByModerator
  ) {
    const when = formatDate(comment.deletedAt);
    const label =
      comment.status === CommentStatus.deletedByUser
        ? `Usunięty przez autora${when ? ` • ${when}` : ''}`
        : `Usunięty przez moderatora${when ? ` • ${when}` : ''}`;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Chip
          icon={<PersonOffIcon fontSize="small" />}
          label={label}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontSize: 11 }}
        />
      </Box>
    );
  }
  return null;
}

export default function Comment({
  comment,
  service,
}: {
  comment: CommentDto,
  service: CommentsService,
}) {
  const { user, isRegularUser } = useUserState();
  const { createdDate } = comment;
  const parent = comment.parent?.user.name;
  const isModerator = !isRegularUser;

  const moderated =
    comment.hidden ||
    (comment.status != null && comment.status !== CommentStatus.default);

  const authorName =
    comment.user.name === 'deleted user'
      ? 'nieaktywny użytkownik'
      : comment.user.name;

  const bodyPlain = `${parent != null ? `@${parent} ` : ''}${comment.content}`;

  const showMenu = comment.user.id === user.id || isModerator;

  return (
    <ListItem
      alignItems="flex-start"
      disableGutters
      sx={{
        paddingRight: '80px',
        ...(isModerator && moderated
          ? {
              borderLeft: '3px solid',
              borderColor:
                comment.hidden || comment.status === CommentStatus.hidden
                  ? 'warning.main'
                  : 'error.main',
              pl: 1,
              opacity: 0.75,
            }
          : {}),
      }}
      secondaryAction={
        <>
          <IconButton onClick={() => service.reply(comment)}>
            <ReplyIcon />
          </IconButton>
          {showMenu && (
            <IconButton onClick={(e) => service.menu(comment, e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          )}
        </>
      }
    >
      <ListItemAvatar>
        <Avatar name={authorName} faded={isModerator && moderated} />
      </ListItemAvatar>
      <ListItemText
        primary={
          <>
            <Username faded={isModerator && moderated}>{authorName}</Username>
            <CreatedDate faded={isModerator && moderated}>
              {timeAgo(new Date(createdDate))}
            </CreatedDate>
            {isModerator && moderated && (
              <ModerationBadge comment={comment} />
            )}
          </>
        }
        secondary={bodyPlain}
        secondaryTypographyProps={{
          sx: {
            whiteSpace: 'break-spaces',
            textAlign: 'justify',
            overflowWrap: 'break-word',
            color: 'text.primary',
          },
        }}
      >
      </ListItemText>
    </ListItem>
  );
}
